import { useCallback, useRef } from 'react';

export type LatestOnlyOutcome<T> = { superseded: false; result: T } | { superseded: true };

/**
 * Serializa las llamadas: nunca hay dos en vuelo y solo la última pedida cuenta.
 * Las que quedaron atrás ni se mandan, y si ya estaban en vuelo su resultado o error se descarta.
 */
export function useLatestOnlyQuote<TArgs, TResult>(send: (args: TArgs) => Promise<TResult>) {
    const tail = useRef<Promise<unknown>>(Promise.resolve());
    const latest = useRef(0);

    const request = useCallback(
        (args: TArgs): Promise<LatestOnlyOutcome<TResult>> => {
            const ticket = ++latest.current;
            const run = tail.current.then(async (): Promise<LatestOnlyOutcome<TResult>> => {
                if (ticket !== latest.current) return { superseded: true };
                try {
                    const result = await send(args);
                    return ticket === latest.current
                        ? { superseded: false, result }
                        : { superseded: true };
                } catch (e) {
                    if (ticket !== latest.current) return { superseded: true };
                    throw e;
                }
            });
            tail.current = run.catch(() => undefined);
            return run;
        },
        [send],
    );

    /** Número del último pedido: permite saber si algo más nuevo llegó mientras se esperaba. */
    const currentTicket = useCallback((): number => latest.current, []);

    return { request, currentTicket };
}
